import type {
  AnalytixParams,
  AnalytixSendMethods,
  AnalytixUserInfo,
  AnyableAnalytixUserInfo,
  SendEventParams,
} from './index.h'

import { STPNORAAnalytix } from '@bldsoft/stpanalytix'
import {
  AnalytixInitializeError,
  AnalytixNotInitializedError,
  AnalytixSendEventError,
} from './errors'
import { ANALYTIX_EVENT_NAMES, debugAnalytixEvent, logger } from './lib'
import { send } from './model'

/**
 * Wrapper class for STPNORAAnalytix
 */
export class Analytix implements AnalytixSendMethods {
  private instance: STPNORAAnalytix | null = null
  private info: AnalytixUserInfo = {}
  private enabled = false

  /**
   * Initialize new internal STPNORAAnalytix instance
   */
  initialize(params: AnalytixParams) {
    try {
      logger.debug('initialize new analytix instance with url:', params.baseUrl)
      this.instance = STPNORAAnalytix.initialize(
        params.baseUrl,
        '', // username placeholder required by upstream signature
        '', // password placeholder required by upstream signature
        params.resultOverflowMode ?? 'break',
        params.configAccountCode,
        params.appName,
        params.appId,
        params.appReleaseVersion,
        params.analyticsVersion ?? '2.5'
      )
    } catch (error) {
      this.instance = null
      this.enabled = false
      throw new AnalytixInitializeError(error)
    }
  }

  /**
   * Enable analytix
   */
  enable() {
    logger.debug('enable analytix')
    this.enabled = true
  }

  /**
   * Disable analytix
   */
  disable() {
    logger.debug('disable analytix')
    this.enabled = false
  }

  set userInfo(info: AnyableAnalytixUserInfo) {
    this.info = info
  }

  //
  // Analytics events
  //

  private debug<T extends keyof AnalytixSendMethods>(
    name: T,
    params: Parameters<AnalytixSendMethods[T]>[0] = {},
    userInfo: AnyableAnalytixUserInfo = {}
  ) {
    const eventName = ANALYTIX_EVENT_NAMES[name]
    const eventEmojis: Record<string, string | undefined> = {
      session_start: '🚀',
      session_end: '👋',
      favorite_add: '❤️',
      favorite_remove: '💔',
      player_open: '📺',
      play_start: '▶️',
      play_stop: '⏸️',
      search: '🔍',
      stream_info: '📡',
      cdn_info: '🌐',
      remote_control_action: '🎮',
      ui_action: '👆',
    }

    debugAnalytixEvent({
      eventName,
      params: params ?? {},
      general: userInfo ?? {},
      emoji: eventEmojis[eventName],
    })
  }

  private async send<T extends keyof AnalytixSendMethods>(
    name: T,
    params: Parameters<AnalytixSendMethods[T]>[0]
  ) {
    if (!this.enabled) return true
    if (!this.instance) throw new AnalytixNotInitializedError()

    // update user info before send
    this.instance.userInfo = this.info

    // debug log event info
    this.debug(name, params, this.info)
    send({ name, params, general: this.info })

    // send event by calling the corresponding method on the instance
    return this.instance[name](params as any).then(
      (result) => {
        if (!result) throw new AnalytixSendEventError(name, false)
        return true
      },
      (error) => {
        throw new AnalytixSendEventError(name, error)
      }
    )
  }

  async sendSessionStartEvent(params: SendEventParams.SessionStart) {
    return this.send('sendSessionStartEvent', params)
  }

  async sendSessionEndEvent(params: SendEventParams.SessionEnd) {
    return this.send('sendSessionEndEvent', params!)
  }

  async sendFavoriteAddEvent(params: SendEventParams.FavoriteAdd) {
    return this.send('sendFavoriteAddEvent', params)
  }

  async sendFavoriteRemoveEvent(params: SendEventParams.FavoriteRemove) {
    return this.send('sendFavoriteRemoveEvent', params)
  }

  async sendPlayerOpenEvent(params: SendEventParams.PlayerOpen) {
    return this.send('sendPlayerOpenEvent', params)
  }

  async sendPlayStartEvent(params: SendEventParams.PlayStart) {
    return this.send('sendPlayStartEvent', params)
  }

  async sendPlayStopEvent(params: SendEventParams.PlayStop) {
    return this.send('sendPlayStopEvent', params)
  }

  async sendSearchEvent(params: SendEventParams.Search) {
    return this.send('sendSearchEvent', params)
  }

  async sendStreamInfoEvent(params: SendEventParams.StreamInfo) {
    return this.send('sendStreamInfoEvent', params)
  }

  async sendCDNInfoEvent(params: SendEventParams.CDNInfo) {
    return this.send('sendCDNInfoEvent', params)
  }

  async sendRemoteControlActionEvent(params: SendEventParams.RCAction) {
    return this.send('sendRemoteControlActionEvent', params)
  }

  async sendUIActionEvent(params: SendEventParams.UIAction) {
    return this.send('sendUIActionEvent', params)
  }
}
